import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Button,
  InputAdornment,
  FormControl,
  MenuItem,
  Select,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import UserLayout from "components/layouts/UserLayout";
import CustomTextField from "components/mui/text-field";
import { useNavigate } from "react-router-dom";
import Icon from "components/icon";
import "assets/CSS/Switch.css";

const InvoiceSync = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Top cards data
  const topCardsData = [
    {
      id: 1,
      value: "127",
      label: "Total Invoices",
      icon: "iconamoon:invoice",
      iconColor: "#3D3D3D",
      bgColor: "#7C8DB51F",
    },
    {
      id: 2,
      value: "$485K",
      label: "Total Value",
      icon: "lsicon:amount-dollar-outline",
      iconColor: "#003A70",
      bgColor: "#f0f0f1",
    },
    {
      id: 3,
      value: "3",
      label: "Synced",
      icon: "material-symbols:sync-rounded",
      iconColor: "#23A26D",
      bgColor: "#E8F5E9",
    },
    {
      id: 4,
      value: "9",
      label: "Errors",
      icon: "bx:error",
      iconColor: "#d32f2f",
      bgColor: "#ffebee",
    },
  ];

  // Invoice data following the design structure
  const invoices = [
    {
      id: "AST-2024-001",
      title: "Complete site excavation including grading and preparation",
      type: "Assessment",
      status: "Sent",
      amount: "$12,500.00",
      syncStatus: "Synced",
      customer: "ABC Construction Corp",
      jobsite: "Downtown Office Building",
      qbNumber: "INV-2024-001",
      lastSync: "2 hours ago",
      items: [
        {
          name: "Site Excavation",
          description: "100 Cubic Yard × $45.00",
          amount: "$4,500.00",
        },
        {
          name: "Concrete Pouring",
          description: "640 Square Foot × $12.50",
          amount: "$8,000.00",
        },
      ],
      created: "1/15/2024",
      due: "2/14/2024",
    },
    {
      id: "WO-2024-002",
      title: "Complete site excavation including grading and preparation",
      type: "Work Order",
      status: "Paid",
      amount: "$8,750.00",
      syncStatus: "Synced",
      customer: "ABC Construction Corp",
      jobsite: "Downtown Office Building",
      qbNumber: "INV-2024-001",
      lastSync: "2 hours ago",
      items: [
        {
          name: "Electrical Installation",
          description: "40 Hour × $85.00",
          amount: "$3,400.00",
        },
        {
          name: "Plumbing Services",
          description: "35 Hour × $75.00",
          amount: "$2,625.00",
        },
        {
          name: "Project Management",
          description: "21 Hour × $125.00",
          amount: "$2,625.00",
        },
      ],
      created: "1/15/2024",
      due: "2/14/2024",
    },
    {
      id: "DEP-2024-003",
      title: "Complete site excavation including grading and preparation",
      type: "Deposit",
      status: "Draft",
      amount: "$25,000.00",
      syncStatus: "Pending",
      customer: "ABC Construction Corp",
      jobsite: "Downtown Office Building",
      qbNumber: "INV-2024-001",
      lastSync: "2 hours ago",
      items: [
        {
          name: "Project Deposit - 50%",
          description: "1 Each × $25,000.00",
          amount: "$25,000.00",
        },
      ],
      created: "1/15/2024",
      due: "2/14/2024",
    },
  ];

  const handlePageBack = () => {
    navigate("/quickbooks");
  };

  const handleResolveClick = (invoice) => {
    setSelectedInvoice(invoice);
    setResolveDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setResolveDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleUseQuickBooksData = () => {
    console.log("Using QuickBooks data for:", selectedInvoice?.id);
    handleCloseDialog();
  };

  const handleUseCleriData = () => {
    console.log("Using Cleri data for:", selectedInvoice?.id);
    handleCloseDialog();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      Sent: { label: "Sent", color: "#003A70", bgColor: "#003A700D" },
      Paid: { label: "Paid", color: "#23A26D", bgColor: "#E8F5E9" },
      Draft: { label: "Draft", color: "#DD992F", bgColor: "#FFF3E0" },
      Pending: { label: "Pending", color: "#DD992F", bgColor: "#FFF3E0" },
      Conflict: { label: "Conflict", color: "#DA1212", bgColor: "#DA12120A" },
    };

    const config = statusConfig[status] || statusConfig.Pending;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          px: 1.5,
        }}
      />
    );
  };

  const getTypeChip = (type) => {
    const typeConfig = {
      Assessment: {
        label: "Assessment",
        color: "primary.main",
        bgColor: "transparent",
      },
      "Work Order": {
        label: "Work Order",
        color: "primary.main",
        bgColor: "transparent",
      },
      Deposit: {
        label: "Deposit",
        color: "primary.main",
        bgColor: "transparent",
      },
    };

    const config = typeConfig[type] || typeConfig.Assessment;

    return (
      <Chip
        label={config.label}
        size="small"
        variant="outlined"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          borderColor: config.color,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          px: 1.5,
          borderRadius: "12px",
        }}
      />
    );
  };

  const getSyncStatusChip = (syncStatus) => {
    const syncConfig = {
      Synced: { label: "Synced", color: "#23A26D", bgColor: "#E8F5E9" },
      Pending: { label: "Pending", color: "#DD992F", bgColor: "#FFF3E0" },
      Error: { label: "Error", color: "#DA1212", bgColor: "#DA12120A" },
    };

    const config = syncConfig[syncStatus] || syncConfig.Pending;

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: config.bgColor,
          color: config.color,
          fontWeight: 600,
          fontSize: "0.75rem",
          height: 24,
          px: 1.5,
        }}
      />
    );
  };

  return (
    <UserLayout
      contentHeightFixed={false}
      NavURL={handlePageBack}
      showIcon={true}
      activeLink="Quickbooks/Invoices"
    >
      <Box sx={{ p: { xs: 1, md: 1 }, mx: "auto" }}>
        {/* Main Card */}
        <Card sx={{ overflow: "hidden" }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 4,
                }}
              >
                <Button
                  variant="contained"
                  color="main"
                  size="medium"
                  startIcon={
                    <Icon icon="heroicons:arrow-path" width={20} height={20} />
                  }
                  disabled={!syncEnabled}
                >
                  Sync Now
                </Button>
              </Box>
            </Box>

            {/* Top Cards Section */}
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={3}>
                {topCardsData.map((card) => (
                  <Grid item xs={12} sm={6} md={3} key={card.id}>
                    <Box
                      sx={{
                        backgroundColor: "background.paper",
                        p: 4,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        minHeight: 140,
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-flex",
                          backgroundColor: card.bgColor,
                          borderRadius: "12px",
                          width: 48,
                          height: 48,
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Icon
                          icon={card.icon}
                          width={24}
                          height={24}
                          style={{ color: card.iconColor }}
                        />
                      </Box>

                      <Box sx={{ textAlign: "right", flex: 1, ml: 2 }}>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 700, mb: 1 }}
                        >
                          {card.value}
                        </Typography>
                        <Typography variant="body1">{card.label}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Main Content Card with Header and Search */}
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                mb: 4,
              }}
            >
              {/* Header and Controls Section */}
              <Box sx={{ p: 4, pb: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Box>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      Invoice Synchronization
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Cleri assessments, work orders, and deposits sync to
                      QuickBooks as invoices.
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CustomTextField
                      placeholder="Search"
                      size="small"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{
                        width: 220,
                        "& .MuiOutlinedInput-root": {
                          height: "36px",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon icon="tabler:search" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">Status</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="sent">Sent</MenuItem>
                        <MenuItem value="paid">Paid</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              {/* Invoices List */}
              <Box sx={{ p: 4, pt: 3 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {invoices.map((invoice) => (
                    <Card
                      key={invoice.id}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "visible",
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        {/* Invoice Header */}
                        <Box sx={{ p: 4 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Typography
                                  variant="h5"
                                  sx={{
                                    fontWeight: 700,
                                    color: "text.primary",
                                  }}
                                >
                                  {invoice.id}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    ml: 5,
                                  }}
                                >
                                  {getTypeChip(invoice.type)}
                                  {getStatusChip(invoice.status)}
                                </Box>
                              </Box>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mb: 2 }}
                              >
                                {invoice.title}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                }}
                              >
                                     <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: 
          invoice.syncStatus === "Sent" ? "#003A70" :
          invoice.syncStatus === "Synced" ? "#23A26D" :
          invoice.syncStatus === "Pending" ? "#DD992F" : "text.primary",
      }}
>
                                  {invoice.amount}
                                </Typography>
                                {getSyncStatusChip(invoice.syncStatus)}
                              </Box>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              backgroundColor: "#003A700D",
                              borderRadius: 1,
                              p: 2,
                              border: "1px solid #e0e0e0",
                              mb: 2,
                            }}
                          >
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Customer
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {invoice.customer}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Jobsite
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {invoice.jobsite}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  QB Number
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {invoice.qbNumber}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6} md={3}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mb: 0.5 }}
                                >
                                  Last Sync
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {invoice.lastSync}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </Box>
                        <Box sx={{ px: 3 }}>
                          <Box
                            sx={{
                              backgroundColor: "#97A4B10D",
                              borderRadius: 1,
                              p: 3,
                              border: "1px solid #e0e0e0",
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                mb: 2,
                                color: "text.primary",
                              }}
                            >
                              Invoice Items
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {invoice.items.map((item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                  }}
                                >
                                  <Box sx={{ flex: 1 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {item.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                    >
                                      {item.description}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 600,
                                      minWidth: "100px",
                                      textAlign: "right",
                                    }}
                                  >
                                    {item.amount}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Box>
                         <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        mt: 3, 
                        mb: -2.5, 
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Icon
                          icon="uit:calender"
                          width={16}
                          height={16}
                          style={{ color: "#23A26D" }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                        >
                          Created: {invoice.created}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                      <Icon
                        icon="uit:calender"
                        width={16}
                        height={16}
                        style={{ color: "#D80027" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600 }}
                      >
                        Due: {invoice.due}
                      </Typography>
                    </Box>
                  </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Card>
          </CardContent>
        </Card>

        {/* Resolve Conflict Dialog */}
        <Dialog
          open={resolveDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Resolve Sync Conflict
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Both systems have updates for {selectedInvoice?.id}. Choose which
              data to keep.
            </Typography>
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* QuickBooks Data Box */}
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  QuickBooks Data
                </Typography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  {selectedInvoice?.items?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {item.amount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Cleri Data Box */}
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Cleri Data
                </Typography>

                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  {selectedInvoice?.items?.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {item.amount}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, gap: 2, justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              onClick={handleCloseDialog}
              sx={{ minWidth: 120 }}
            >
              Cancel
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleUseQuickBooksData}
                sx={{ minWidth: 160 }}
              >
                Use QuickBooks Data
              </Button>
              <Button
                variant="contained"
                color="main"
                onClick={handleUseCleriData}
                sx={{ minWidth: 120 }}
              >
                Use Cleri Data
              </Button>
            </Box>
          </DialogActions>
        </Dialog>
      </Box>
    </UserLayout>
  );
};

export default InvoiceSync;
